import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import { DoubleShift, TripleShift } from "../../types/Shift";

function toIsoDate(value: string): string;
function toIsoDate(value: string | null): string | null;
function toIsoDate(value: string | null): string | null {
  if (!value) return value;
  return DateTime.fromISO(value).toISODate() ?? value;
}

interface ShiftState {
  selectedDate: string;
  selectedShift: TripleShift;
  // Note: the system's latest shift (latest tblShift record), fetched by ShiftLoginBootstrapper.
  // Persisted so the offline complete-shift flow can tell whether the selected shift is the
  // latest without a server call or any reliance on DateTime.now().
  latestShiftDate: string | null;
  latestShift: TripleShift | null;
  // Note: the shift a completion queued while offline has provisionally created. Nothing can report the
  // lock state of a shift the server has never seen, so it is treated as editable until the completion
  // syncs.
  provisionalShiftDate: string | null;
  provisionalShift: DoubleShift | null;
  shiftStartTime: string | null; // ISO UTC based date
  shiftEndTime: string | null;
  isLocked: boolean;
  canToggleLock: boolean;
  isPastAutoLockCutOff: boolean | null;
  shiftExists: boolean;
  isShiftEditibilityDataLoading: boolean;
  shiftEditabilityFetched: boolean;
}

const initialState: ShiftState = {
  selectedDate: toIsoDate(localStorage.getItem("redux_selectedDate")) || DateTime.now().toISODate(),
  selectedShift: (localStorage.getItem("redux_selectedShift") as TripleShift) || 'DAY',
  latestShiftDate: toIsoDate(localStorage.getItem("redux_latestShiftDate")),
  latestShift: (localStorage.getItem("redux_latestShift") as TripleShift) || null,
  provisionalShiftDate: toIsoDate(localStorage.getItem("redux_provisionalShiftDate")),
  provisionalShift: (localStorage.getItem("redux_provisionalShift") as DoubleShift) || null,
  shiftStartTime: null,
  shiftEndTime: null,
  isLocked: false,
  canToggleLock: false,
  isPastAutoLockCutOff: null,
  shiftExists: true,
  isShiftEditibilityDataLoading: false,
  shiftEditabilityFetched: false,
};

const selectedShiftSlice = createSlice({
  name: 'selectedShift',
  initialState,
  reducers: {
    setSelectedDate(state, action: PayloadAction<string>) {
      state.selectedDate = toIsoDate(action.payload);
      localStorage.setItem("redux_selectedDate", state.selectedDate);
    },
    setSelectedShift(state, action: PayloadAction<TripleShift>) {
      state.selectedShift = action.payload;
      localStorage.setItem("redux_selectedShift", action.payload);
    },
    setLatestShift(state, action: PayloadAction<{ date: string; shift: TripleShift }>) {
      state.latestShiftDate = toIsoDate(action.payload.date);
      state.latestShift = action.payload.shift;
      localStorage.setItem("redux_latestShiftDate", state.latestShiftDate);
      localStorage.setItem("redux_latestShift", action.payload.shift);
    },
    setProvisionalShift(state, action: PayloadAction<{ date: string; shift: DoubleShift }>) {
      state.provisionalShiftDate = toIsoDate(action.payload.date);
      state.provisionalShift = action.payload.shift;
      localStorage.setItem("redux_provisionalShiftDate", state.provisionalShiftDate);
      localStorage.setItem("redux_provisionalShift", action.payload.shift);
    },
    clearProvisionalShift(state) {
      state.provisionalShiftDate = null;
      state.provisionalShift = null;
      localStorage.removeItem("redux_provisionalShiftDate");
      localStorage.removeItem("redux_provisionalShift");
    },
    setShiftBoundaries(state, action: PayloadAction<{ start: string; end: string }>) {
      state.shiftStartTime = action.payload.start;
      state.shiftEndTime = action.payload.end;
    },
    setShiftEditability(state, action: PayloadAction<{ isLocked: boolean; canToggleLock: boolean; isPastAutoLockCutOff: boolean | null; exists?: boolean }>) {
      state.isLocked = action.payload.isLocked;
      state.canToggleLock = action.payload.canToggleLock;
      state.isPastAutoLockCutOff = action.payload.isPastAutoLockCutOff;
      state.shiftExists = action.payload.exists ?? true;
      state.shiftEditabilityFetched = true;
    },
    setShiftEditabilityDataLoading(state, action: PayloadAction<boolean>) {
      state.isShiftEditibilityDataLoading = action.payload;
    }
  },
});

export const {
  setSelectedDate,
  setSelectedShift,
  setLatestShift,
  setProvisionalShift,
  clearProvisionalShift,
  setShiftBoundaries,
  setShiftEditability,
  setShiftEditabilityDataLoading
} = selectedShiftSlice.actions;
export default selectedShiftSlice.reducer;
